'use client';

import { cn } from '../../../../lib/utils';
import {
  LoadingIndicator,
  LoadingIndicatorProps,
} from '../../../primitives/feedback-and-status';
import { ArrowDropUp, Close } from '@crabs-ai/web-icons';
import React, {
  createContext,
  JSX,
  useContext,
  useMemo,
  useState,
} from 'react';

// QueueDock 컨텍스트 타입 정의
type QueueDockContextType = {
  isOpen: boolean;
  toggle: () => void;
};

// QueueDock 컨텍스트 생성
const QueueDockContext = createContext<QueueDockContextType | undefined>(
  undefined,
);

// 컨텍스트 훅 생성
function useQueueDock() {
  const context = useContext(QueueDockContext);
  if (!context) {
    throw new Error(
      'QueueDock 컴포넌트는 QueueDock 내부에서만 사용할 수 있습니다',
    );
  }
  return context;
}

// QueueDock 루트 컴포넌트 props
interface QueueDockRootProps {
  /** 열림 여부 */
  isOpen?: boolean;
  /** 기본 상태 여부 */
  isDefaultOpen?: boolean;
  /** 열림 여부 변경 핸들러 */
  onOpenChange?: (isOpen: boolean) => void;
  /** 클래스명 */
  className?: string;
  /** children 노드 */
  children?: React.ReactNode;
}

// QueueDock 헤더 컴포넌트 props
interface QueueDockHeaderProps {
  /** 닫기 버튼 여부 */
  isShowCloseButton?: boolean;
  /** 닫기 버튼 핸들러 */
  onClose?: () => void;
  /** 클래스명 */
  className?: string;
  /** 헤더에 표시될 제목 노드 */
  children: React.ReactNode;
}

// QueueDock 컨텐츠 컴포넌트 props
interface QueueDockContentProps {
  /** 클래스명 */
  className?: string;
  /** children 노드 */
  children?: React.ReactNode;
  /** 최대 높이 */
  maxHeight?: number;
}

// QueueDock 프로그레스 컴포넌트 props
interface QueueDockProgressProps {
  /** 클래스명 */
  className?: string;
  /** 사이즈 */
  size?: LoadingIndicatorProps['size'];
}

// 하위 컴포넌트 타입에 displayName 추가
type QueueDockSubComponent<P = object> = {
  (props: P): JSX.Element;
  displayName?: string;
};

// QueueDock 컴포넌트와 하위 컴포넌트들의 타입
interface QueueDockCompound {
  (props: QueueDockRootProps): JSX.Element;
  Header: QueueDockSubComponent<QueueDockHeaderProps>;
  Content: QueueDockSubComponent<QueueDockContentProps>;
  Progress: QueueDockSubComponent<QueueDockProgressProps>;
  displayName?: string;
}

// QueueDock 구현
const QueueDock: QueueDockCompound = ({
  isOpen: externalIsOpen,
  isDefaultOpen = false,
  onOpenChange,
  className,
  children,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(isDefaultOpen);

  const isOpen = useMemo(
    () => (externalIsOpen !== undefined ? externalIsOpen : internalIsOpen),
    [externalIsOpen, internalIsOpen],
  );

  const toggle = () => {
    const newState = !isOpen;
    if (onOpenChange) {
      onOpenChange(newState);
    } else {
      setInternalIsOpen(newState);
    }
  };

  // QueueDock의 자식 요소 중 Progress가 있는지 확인
  const hasProgress = useMemo(() => {
    let hasProgressChild = false;
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === QueueDock.Progress) {
        hasProgressChild = true;
      }
    });
    return hasProgressChild;
  }, [children]);

  return (
    <QueueDockContext.Provider value={{ isOpen, toggle }}>
      <div
        className={cn(
          `w-full overflow-hidden rounded-t-xs border-x-[1px] border-t-[1px] ${!hasProgress && 'border-b-[1px]'} border-sys-style-neutral-default`,
          className,
        )}
      >
        {children}
      </div>
    </QueueDockContext.Provider>
  );
};

// QueueDock.Header 컴포넌트
QueueDock.Header = function QueueDockHeader({
  isShowCloseButton = false,
  onClose,
  className,
  children,
}: QueueDockHeaderProps) {
  const { isOpen, toggle } = useQueueDock();

  return (
    <div
      className={cn(
        'flex cursor-pointer items-center justify-between gap-2xs border-b-[1px] border-b-sys-style-neutral-default bg-sys-bg-surface-2 px-md py-sm',
        className,
      )}
      onClick={toggle}
    >
      <div className="flex items-center text-comp-accent-medium">
        {children}
      </div>
      <div className="flex gap-2xs">
        <ArrowDropUp
          className={`size-[24px] shrink-0 transition-all [&_path]:fill-sys-cont-neutral-default ${isOpen ? '' : 'rotate-180'}`}
        />
        {isShowCloseButton && (
          <div
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
          >
            <Close className="size-[24px] shrink-0 [&_path]:fill-sys-cont-neutral-default" />
          </div>
        )}
      </div>
    </div>
  );
};

// QueueDock.Content 컴포넌트
QueueDock.Content = function QueueDockContent({
  children,
  maxHeight = 256,
  className,
}: QueueDockContentProps) {
  const { isOpen } = useQueueDock();

  const height = isOpen ? maxHeight : 0;
  return (
    <div
      className={cn(
        `flex h-full w-full flex-col overflow-y-auto bg-sys-bg-surface-1 p-2 transition-all`,
        className,
        isOpen ? 'opacity-100' : 'py-0 opacity-0',
      )}
      style={{
        maxHeight: `${height}px`,
      }}
    >
      {children}
    </div>
  );
};

// QueueDock.Progress 컴포넌트
QueueDock.Progress = function QueueDockProgress({
  className,
  size = 'sm',
}: QueueDockProgressProps) {
  return (
    <LoadingIndicator
      size={size}
      type="linear"
      className={className}
    />
  );
};

QueueDock.displayName = 'QueueDock';
QueueDock.Header.displayName = 'QueueDock.Header';
QueueDock.Content.displayName = 'QueueDock.Content';
QueueDock.Progress.displayName = 'QueueDock.Progress';

export default QueueDock;
